// components/CambioHabitacion.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Typography,
  message,
  DatePicker,
  Card,
  Row,
  Col,
  Spin,
  Alert,
  Divider,
  Tag,
  Tooltip
} from 'antd';
import {
  SwapOutlined,
  CloseOutlined,
  CheckOutlined,
  CalendarOutlined,
  HomeOutlined,
  UserOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CambioHabitacion = ({
  visible,
  onCancel,
  onSuccess,
  reservaData,
  desdePlanning = true
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [habitacionDestinoValida, setHabitacionDestinoValida] = useState(false);
  const [habitacionDestinoInfo, setHabitacionDestinoInfo] = useState(null);
  const [fechaCambio, setFechaCambio] = useState(null);
  const [infoHabitacionOrigen, setInfoHabitacionOrigen] = useState(null);
  const [habitacionDestino, setHabitacionDestino] = useState('');

  // Inicializar datos cuando se abre el modal
  useEffect(() => {
    if (visible && reservaData) {
      // Determinar qué formato de datos estamos recibiendo
      const fechaEntrada = reservaData.Desde || reservaData.FechaEntrada;
      const fechaSalida = reservaData.Hasta || reservaData.FechaSalida;
      const numHabitacion = reservaData.NumHabitacion || reservaData.NumHabitacio;
      
      const fechaEntradaObj = dayjs(fechaEntrada);
      const fechaSalidaObj = dayjs(fechaSalida);
      
      // Fecha de cambio por defecto = fecha de entrada
      const fechaCambioDefault = fechaEntradaObj;
      
      setFechaCambio(fechaCambioDefault);
      setHabitacionDestino('');
      setHabitacionDestinoValida(false);
      setHabitacionDestinoInfo(null);
      
      form.setFieldsValue({
        numReserva: reservaData.NumReserva,
        numLinea: reservaData.NumLinea || 1,
        nombre: reservaData.Nombre,
        habitacionOrigen: numHabitacion,
        fechaEntrada: fechaEntradaObj,
        fechaSalida: fechaSalidaObj,
        fechaCambio: fechaCambioDefault,
        habitacionDestino: '',
        observacion: reservaData.Observacion || ''
      });
      
      // Cargar información de la habitación origen
      cargarInfoHabitacion(numHabitacion);
    }
  }, [visible, reservaData, form]);

  // Verificar habitación destino al cambiar el valor
  const verificarHabitacionDestino = async (habitacionDestinoValue) => {
    if (!habitacionDestinoValue || habitacionDestinoValue === '') {
      setHabitacionDestinoValida(false);
      setHabitacionDestinoInfo(null);
      setHabitacionDestino('');
      return false;
    }
    
    const numHabitacion = parseInt(habitacionDestinoValue);
    if (isNaN(numHabitacion)) {
      setHabitacionDestinoValida(false);
      setHabitacionDestinoInfo(null);
      setHabitacionDestino('');
      return false;
    }
    
    setVerificando(true);
    try {
      const token = localStorage.getItem('token');
      
      // Verificar si la habitación existe
      const response = await axios.get(`${BASE_URL}/api/habitaciones/obtenerhabitacion/${numHabitacion}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
    //   console.log(" habitacion => ",response.data.data);
      
      if (response.data.success && response.data.data) {
        const habitacion = response.data.data;
        setHabitacionDestinoInfo(habitacion);
        setHabitacionDestino(habitacionDestinoValue);
        
        // Verificar que no sea la misma habitación
        const habitacionOrigen = form.getFieldValue('habitacionOrigen');
        if (numHabitacion === parseInt(habitacionOrigen)) {
          message.warning('La habitación de destino no puede ser la misma que la actual');
          setHabitacionDestinoValida(false);
          setVerificando(false);
          return false;
        }
        
        // Verificar disponibilidad para el rango de fechas
        const fechaCambioValue = fechaCambio || dayjs(reservaData?.Desde || reservaData?.FechaEntrada);
        const fechaEntrada = dayjs(reservaData?.Desde || reservaData?.FechaEntrada);
        const fechaSalida = dayjs(reservaData?.Hasta || reservaData?.FechaSalida);
        
        const disponible = await verificarDisponibilidad(numHabitacion, fechaCambioValue, fechaEntrada, fechaSalida);
        
        if (disponible) {
          setHabitacionDestinoValida(true);
          return true;
        } else {
          setHabitacionDestinoValida(false);
          return false;
        }
      } else {
        message.error('La habitación no existe');
        setHabitacionDestinoValida(false);
        setHabitacionDestinoInfo(null);
        setHabitacionDestino('');
        return false;
      }
    } catch (error) {
      console.error('Error verificando habitación:', error);
      message.error(error.response?.data?.error || 'Error al verificar la habitación');
      setHabitacionDestinoValida(false);
      setHabitacionDestinoInfo(null);
      setHabitacionDestino('');
      return false;
    } finally {
      setVerificando(false);
    }
  };

  // Verificar disponibilidad de la habitación destino
  const verificarDisponibilidad = async (habitacionDestinoNum, fechaCambioValue, fechaEntrada, fechaSalida) => {
    try {
      const token = localStorage.getItem('token');
      
      // Verificar si la habitación está ocupada en el rango de fechas
      const response = await axios.get(`${BASE_URL}/api/reservas/disponibilidad-habitacion`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
        //   fechaDesde: fechaCambioValue.format('YYYY-MM-DD'),
        //   fechaHasta: fechaSalida.format('YYYY-MM-DD'),
        //   reservaExcluir: reservaData?.NumReserva
          numhabitacion:habitacionDestinoNum,
          entrada:fechaCambioValue.format('YYYY-MM-DD'),
          salida:fechaSalida.format('YYYY-MM-DD'),
          excluirReserva:reservaData?.NumReserva
        }
      });
      
      if (response.data.success) {
        const disponible = response.data.data.disponible;
        if (!disponible) {
          message.warning(`La habitación ${habitacionDestinoNum} no está disponible en el rango de fechas seleccionado`);
          return false;
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error verificando disponibilidad:', error);
      return false;
    }
  };

  // Cargar información de la habitación origen
  const cargarInfoHabitacion = async (numHabitacion) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitaciones/${numHabitacion}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setInfoHabitacionOrigen(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando información habitación:', error);
    }
  };

  // Validar fecha de cambio
  const validarFechaCambio = (fecha) => {
    const fechaEntrada = dayjs(reservaData?.Desde || reservaData?.FechaEntrada);
    const fechaSalida = dayjs(reservaData?.Hasta || reservaData?.FechaSalida);
    
    if (fecha.isBefore(fechaEntrada) || fecha.isAfter(fechaSalida.subtract(1, 'day'))) {
      message.error('La fecha de cambio debe estar entre la fecha de entrada y la fecha de salida (excluyendo el último día)');
      return false;
    }
    return true;
  };

  // Manejar cambio de fecha
  const handleFechaCambioChange = async (date) => {
    if (date && validarFechaCambio(date)) {
      setFechaCambio(date);
      form.setFieldsValue({ fechaCambio: date });
      
      // Re-verificar disponibilidad si hay habitación destino seleccionada
      if (habitacionDestino && habitacionDestino !== '') {
        const fechaEntrada = dayjs(reservaData?.Desde || reservaData?.FechaEntrada);
        const fechaSalida = dayjs(reservaData?.Hasta || reservaData?.FechaSalida);
        const disponible = await verificarDisponibilidad(parseInt(habitacionDestino), date, fechaEntrada, fechaSalida);
        setHabitacionDestinoValida(disponible);
        if (!disponible) {
          message.warning(`La habitación ${habitacionDestino} no está disponible en la nueva fecha`);
        }
      }
    }
  };

  // Manejar cambio de habitación destino
  const handleHabitacionDestinoChange = async (e) => {
    const value = e.target.value;
    setHabitacionDestino(value);
    if (value && value !== '') {
      await verificarHabitacionDestino(value);
    } else {
      setHabitacionDestinoValida(false);
      setHabitacionDestinoInfo(null);
    }
  };

  // Ejecutar cambio de habitación
  const handleCambiar = async () => {
    try {
      const values = await form.validateFields();
      
      const numReserva = values.numReserva;
      const numLinea = values.numLinea;
      const habitacionOrigen = parseInt(values.habitacionOrigen);
      const habitacionDestinoNum = parseInt(habitacionDestino);
      const fechaCambioValue = values.fechaCambio;
      const observacion = values.observacion || '';
      
      if (habitacionOrigen === habitacionDestinoNum) {
        message.warning('La habitación de destino es la misma que la actual');
        return;
      }
      
      if (!validarFechaCambio(fechaCambioValue)) {
        return;
      }
      
      setLoading(true);
      
      const token = localStorage.getItem('token');
      
      // Llamar al API para realizar el cambio
      const response = await axios.post(
        `${BASE_URL}/api/reservas/${numReserva}/cambiar-habitacion`,
        {
          numLinea,
          habitacionOrigen,
          habitacionDestino: habitacionDestinoNum,
          fechaCambio: fechaCambioValue.format('YYYY-MM-DD'),
          observacion
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        message.success('Habitación cambiada exitosamente');
        onSuccess && onSuccess(response.data.data);
        onCancel();
      } else {
        message.error(response.data.error || 'Error al realizar el cambio');
      }
    } catch (error) {
      console.error('Error cambiando habitación:', error);
      message.error(error.response?.data?.error || 'Error al realizar el cambio');
    } finally {
      setLoading(false);
    }
  };

  // Calcular estado de validez del formulario
  const isFormValid = () => {
    return habitacionDestino && habitacionDestino !== '' && habitacionDestinoValida;
  };

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined style={{ color: '#1890ff' }} />
          <span style={{ fontWeight: 600 }}>Cambio de Habitación</span>
          {reservaData && (
            <Tag color="blue">Reserva #{reservaData.NumReserva}</Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
      maskClosable={false}
    >
      {reservaData ? (
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              numLinea: 1
            }}
          >
            {/* Datos de la reserva actual */}
            <Card 
              size="small" 
              style={{ 
                marginBottom: 16, 
                backgroundColor: '#f5f5f5',
                borderRadius: 8
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Space>
                    <UserOutlined style={{ color: '#1890ff' }} />
                    <Text strong>{reservaData.Nombre}</Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space>
                    <HomeOutlined style={{ color: '#1890ff' }} />
                    <Text>Hab. {reservaData.NumHabitacion || reservaData.NumHabitacio}</Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    <Text>Entrada: {dayjs(reservaData.Desde || reservaData.FechaEntrada).format('DD/MM/YYYY')}</Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    <Text>Salida: {dayjs(reservaData.Hasta || reservaData.FechaSalida).format('DD/MM/YYYY')}</Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Campos ocultos */}
            <Form.Item name="numReserva" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="numLinea" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="habitacionOrigen" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="nombre" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="fechaEntrada" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="fechaSalida" hidden>
              <Input />
            </Form.Item>

            {/* Fecha de cambio */}
            <Form.Item
              name="fechaCambio"
              label="Fecha de Cambio"
              rules={[{ required: true, message: 'Seleccione la fecha de cambio' }]}
              tooltip="Fecha a partir de la cual se cambiará la habitación"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                onChange={handleFechaCambioChange}
                disabledDate={(current) => {
                  if (!reservaData) return false;
                  const fechaEntrada = dayjs(reservaData.Desde || reservaData.FechaEntrada);
                  const fechaSalida = dayjs(reservaData.Hasta || reservaData.FechaSalida);
                  return current && (current < fechaEntrada.startOf('day') || current >= fechaSalida.startOf('day'));
                }}
              />
            </Form.Item>

            {/* Habitación destino */}
            <Form.Item
              name="habitacionDestino"
              label="Habitación Destino"
              rules={[
                { required: true, message: 'Ingrese la habitación de destino' },
                {
                  validator: async (_, value) => {
                    if (!value) return Promise.resolve();
                    const num = parseInt(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Ingrese un número de habitación válido'));
                    }
                    const habitacionOrigen = form.getFieldValue('habitacionOrigen');
                    if (num === parseInt(habitacionOrigen)) {
                      return Promise.reject(new Error('La habitación de destino no puede ser la misma que la actual'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              tooltip="Ingrese el número de la habitación destino"
            >
              <Input
                placeholder="Ej: 101"
                prefix={<HomeOutlined />}
                suffix={
                  verificando ? <Spin size="small" /> :
                  habitacionDestinoValida ? <CheckOutlined style={{ color: '#52c41a' }} /> :
                  habitacionDestino ? <CloseOutlined style={{ color: '#ff4d4f' }} /> : null
                }
                onChange={handleHabitacionDestinoChange}
                value={habitacionDestino}
              />
            </Form.Item>

            {/* Información de la habitación destino */}
            {habitacionDestinoInfo && habitacionDestinoValida && (
              <Alert
                message="Habitación disponible"
                description={
                  <div>
                    <Text>Tipo: {habitacionDestinoInfo.TipoHabitacion || 'N/A'}</Text>
                    {habitacionDestinoInfo.Capacidad && (
                      <Text style={{ marginLeft: 16 }}>Capacidad: {habitacionDestinoInfo.Capacidad} pax</Text>
                    )}
                  </div>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Observación */}
            <Form.Item
              name="observacion"
              label="Observación"
              tooltip="Motivo del cambio o información adicional"
            >
              <TextArea
                rows={3}
                placeholder="Ingrese una observación sobre el cambio..."
                maxLength={200}
                showCount
              />
            </Form.Item>

            <Divider />

            {/* Advertencia */}
            <Alert
              message="Importante"
              description={
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  <li>El cambio afectará todas las reservas y cargos asociados a esta línea de reserva</li>
                  <li>Se registrará el cambio en el historial de la reserva</li>
                  <li>La fecha de cambio debe estar entre la entrada y la salida (excluyendo el último día)</li>
                </ul>
              }
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: 16 }}
            />

            {/* Botones de acción */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button onClick={onCancel}>
                Cancelar
              </Button>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                onClick={handleCambiar}
                loading={loading}
                disabled={!isFormValid()}
              >
                Cambiar Habitación
              </Button>
            </div>
          </Form>
        </Spin>
      ) : (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export default CambioHabitacion;